/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'
import i18n from '../i18n'

/* ========== INVITATIONS ========== */
export function useInvitations(companyId) {
    const [invitations, setInvitations] = useState([])

    const fetch = useCallback(async () => {
        if (!companyId) return
        const { data } = await kanbify.from('company_invitations')
            .select('*, invited_by_profile:invited_by(full_name)')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
        setInvitations(data || [])
    }, [companyId])

    useEffect(() => { fetch() }, [fetch])

    const invite = async ({ email, invitedBy, companyName, invitedByName, message }) => {
        // Block inviting an email that already has an account: signup()
        // rejects existing emails, so the invite link is a dead-end and
        // the pending row is just confusing. Check BEFORE creating any row.
        const { data: taken } = await kanbify.rpc('email_taken', { p_email: email })
        if (taken && (taken.taken === true || taken === true)) {
            return { error: i18n.t('errors.accountExistsInvite') }
        }

        const { data: existing } = await kanbify.from('company_invitations')
            .select('id, status').eq('company_id', companyId).eq('email', email).single()

        if (existing && existing.status === 'pending') {
            return { error: i18n.t('errors.invitationAlreadyPending') }
        }

        // Upsert invitation record
        // Generate the invite token client-side. The Postgres schema used
        // a DB default to mint it; the MariaDB migration dropped that, so
        // without this the token stays NULL → broken `?invite=` link and
        // accept_invitation can never match. Fresh token on every (re)invite.
        const inviteToken = (crypto?.randomUUID?.() || `inv_${Date.now()}_${Math.random().toString(36).slice(2)}`)
        const { data: inv, error } = await kanbify.from('company_invitations')
            .upsert([{
                company_id: companyId,
                email,
                invited_by: invitedBy,
                message,
                token: inviteToken,
                status: 'pending',
                expires_at: new Date(Date.now() + 7 * 86400000).toISOString()
            }], { onConflict: 'company_id,email' })
            .select().single()

        if (error) return { error: error.message }

        // Fetch the actual token
        const { data: fullInv } = await kanbify.from('company_invitations')
            .select('token').eq('id', inv.id).single()

        // Call Edge Function to send email
        const fnRes = await kanbify.functions.invoke('send-invitation', {
            body: {
                email, companyId, companyName, invitedByName,
                message, token: fullInv?.token
            }
        })

        await fetch()
        return { data: inv, email_sent: !fnRes.error }
    }

    const cancel = async (invId) => {
        const { error } = await kanbify.from('company_invitations').update({ status: 'rejected' }).eq('id', invId)
        if (error) logDevError('Failed to cancel invitation:', error.message)
        if (!error) setInvitations(p => p.map(i => i.id === invId ? { ...i, status: 'rejected' } : i))
        return { success: !error, error: error?.message || null }
    }

    const resend = async (invId, { companyName, invitedByName } = {}) => {
        // The old impl only bumped status/expires_at and NEVER re-sent the
        // email (and pre-token-fix rows have token=NULL → dead ?invite=
        // link). Now: ensure a token, re-arm pending/expiry, then actually
        // send the invitation email.
        const { data: row } = await kanbify.from('company_invitations')
            .select('id, email, company_id, token').eq('id', invId).single()
        if (!row) return { success: false, error: i18n.t('errors.invitationNotFound') }
        // Don't re-send to an email that now has an account (dead-end link).
        const { data: taken } = await kanbify.rpc('email_taken', { p_email: row.email })
        if (taken && (taken.taken === true || taken === true)) {
            return { success: false, error: i18n.t('errors.accountAlreadyExistsNoReinvite') }
        }
        const token = row.token
            || (crypto?.randomUUID?.() || `inv_${Date.now()}_${Math.random().toString(36).slice(2)}`)
        const { error } = await kanbify.from('company_invitations')
            .update({ status: 'pending', token, expires_at: new Date(Date.now() + 7 * 86400000).toISOString() })
            .eq('id', invId)
        if (error) { logDevError('Failed to resend invitation:', error.message); return { success: false, error: error.message } }
        const fnRes = await kanbify.functions.invoke('send-invitation', {
            body: { email: row.email, companyId: row.company_id, companyName, invitedByName, token },
        })
        await fetch()
        return { success: true, email_sent: !fnRes.error, error: null }
    }

    return { invitations, fetch, invite, cancel, resend }
}

/* ========== JOIN REQUESTS ========== */
export function useJoinRequests(companyId) {
    const [requests, setRequests] = useState([])

    const fetch = useCallback(async () => {
        if (!companyId) return
        const { data } = await kanbify.from('join_requests')
            .select('*, profiles:user_id(id, full_name, email, avatar_url, role, points, level)')
            .eq('company_id', companyId)
            .order('created_at', { ascending: false })
        setRequests(data || [])
    }, [companyId])

    useEffect(() => { fetch() }, [fetch])

    const accept = async (requestId) => {
        const { error } = await kanbify.rpc('accept_join_request', { p_request_id: requestId })
        if (error) logDevError('Failed to accept join request:', error.message)
        if (!error) setRequests(p => p.map(r => r.id === requestId ? { ...r, status: 'accepted' } : r))
        return { success: !error, error: error?.message || null }
    }

    const reject = async (requestId) => {
        const { error } = await kanbify.from('join_requests').update({ status: 'rejected' }).eq('id', requestId)
        if (error) logDevError('Failed to reject join request:', error.message)
        if (!error) setRequests(p => p.map(r => r.id === requestId ? { ...r, status: 'rejected' } : r))
        return { success: !error, error: error?.message || null }
    }

    return { requests, fetch, accept, reject }
}

export async function sendJoinRequest(companyId, userId, message = '') {
    const { data, error } = await kanbify.from('join_requests')
        .upsert([{ company_id: companyId, user_id: userId, message, status: 'pending' }],
            { onConflict: 'company_id,user_id' })
        .select().single()
    return { data, error }
}

export async function acceptInvitationByToken(token, userId) {
    const { data, error } = await kanbify.rpc('accept_invitation', {
        p_token: token, p_user_id: userId
    })
    return { data, error }
}
